// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CostInfoTooltip } from '@/components/cost-info-tooltip';
import { MinuteDuration } from '@/components/minute-duration';
import { SupportSnackbar } from '@/components/support-snackbar';
import { DeleteRounded, EditRounded } from '@/constants/hardfin-standard-icons';
import { deleteCost } from '@/costs/api/delete-cost';
import { CostDialog } from '@/costs/components/cost-dialog';
import { TimeRecordDialog } from '@/costs/components/time-record-dialog';
import { Currency } from '@/currency/api/types';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { useDisclosure } from '@/hooks/use-disclosure';
import { deleteProjectCost } from '@/project/api/delete-project-cost';
import { ProjectCost } from '@/project/api/types';
import { SparseProject } from '@/sparse/api/types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Decimal } from 'decimal.js';

// A TIME cost's hourly rate is delivered as a plain amount string plus an ISO
// code in costData; rebuild the currency object for display and edit seeding.
function toTimeCostRate(cost: ProjectCost): Currency | null {
  if (cost.type !== 'TIME' || !cost.costData.rate) {
    return null;
  }

  const currencyCode =
    cost.costData.rateCurrency ?? cost.amount?.isoCode ?? null;
  if (currencyCode === null) {
    return null;
  }

  return { amount: new Decimal(cost.costData.rate), isoCode: currencyCode };
}

interface Props {
  /**
   * The project to which the cost belongs.
   */
  project: SparseProject;

  /**
   * A project cost to display in the table.
   */
  cost: ProjectCost;

  /**
   * If true, the row is disabled.
   */
  disabled?: boolean;

  /**
   * Whether the user can view time rates
   */
  canViewRates: boolean;
}

/**
 * A table row that displays a cost's amount, description, and actions.
 */
export function ProjectCostTableRow(props: Props) {
  const editDialog = useDisclosure();
  const deleteDialog = useDisclosure();
  const queryClient = useQueryClient();

  const timeCostRate = toTimeCostRate(props.cost);

  const deleteProjectCostMutation = useMutation({
    mutationFn: () => {
      if (props.cost.type === 'TIME') {
        // Time costs use the old project endpoint
        return deleteProjectCost(props.project.key, props.cost.key);
      }
      // Normal costs use the new cost endpoint
      return deleteCost(props.cost.key);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['project', props.project.key],
      });

      // Invalidate specific queries based on cost type
      if (props.cost.type === 'TIME') {
        queryClient.invalidateQueries({
          queryKey: ['time-record-aggregate'],
        });
        queryClient.invalidateQueries({
          queryKey: ['time-records'],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ['cost-aggregate'],
        });
        queryClient.invalidateQueries({
          queryKey: ['costs'],
        });
      }

      deleteDialog.close();
    },
  });

  const handleClickDelete = () => {
    deleteProjectCostMutation.mutate();
  };

  return (
    <>
      <SupportSnackbar
        isOpen={deleteProjectCostMutation.isError}
        onClose={deleteProjectCostMutation.reset}
        message="An error occurred while deleting the cost."
      />
      {props.cost.type === 'TIME' ? (
        <TimeRecordDialog
          initialProject={props.project}
          initialTimeRecord={{
            id: props.cost.id,
            key: props.cost.key,
            category: props.cost.category || '',
            totalAmount: props.cost.amount,
            totalMinutes: props.cost.costData.minutes,
            rate: timeCostRate,
            description: props.cost.description,
            incurredOn: props.cost.costIncurredOn,
            worker: props.cost.costData.employee,
            attachedProject: props.cost.attachedToProject,
          }}
          isOpen={editDialog.isOpen}
          onClose={editDialog.close}
          invalidationKeys={[
            ['project', props.project.key, 'cost'],
            ['time-record-aggregate'],
            ['time-records'],
          ]}
        />
      ) : (
        <CostDialog
          initialProject={props.project}
          initialCost={{
            key: props.cost.key,
            amount: props.cost.amount,
            description: props.cost.description || '',
            category: props.cost.category || null,
            costIncurredOn: props.cost.costIncurredOn,
            project: props.cost.attachedToProject || props.project,
            person: props.cost.attachedToPerson || undefined,
          }}
          isOpen={editDialog.isOpen}
          onClose={editDialog.close}
          invalidationKeys={[
            ['project', props.project.key, 'cost'],
            ['cost-aggregate'],
            ['costs'],
          ]}
        />
      )}
      <Dialog
        fullWidth
        maxWidth="sm"
        open={deleteDialog.isOpen}
        onClose={deleteDialog.close}
      >
        <DialogTitle>Delete cost</DialogTitle>
        <DialogContent dividers>
          <Typography marginBottom={1}>
            Are you sure you want to delete this cost? The cost will be
            permanently removed from the project.
          </Typography>
          <Typography>
            (Note: your audit log will continue to show a record of the deleted
            cost.)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={deleteDialog.close}>
            No, cancel
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            onClick={handleClickDelete}
          >
            Yes, delete
          </Button>
        </DialogActions>
      </Dialog>
      <TableRow>
        <TableCell>
          <Typography variant="body2">
            {props.cost.costIncurredOn.toFormat('MMM d, yyyy')}
          </Typography>
        </TableCell>
        <TableCell>
          {props.cost.description ? (
            <Typography variant="body2">
              {props.cost.description}
              {props.cost.type === 'TIME' && (
                <Typography
                  color={'text.secondary'}
                  variant="body2"
                  component="span"
                >
                  {' '}
                  ({props.cost.costData.employee.firstName}{' '}
                  {props.cost.costData.employee.lastName},{' '}
                  <MinuteDuration
                    minutes={props.cost.costData.minutes}
                    variant="body2"
                    component="span"
                  />
                  {props.canViewRates && props.cost.costData.rate && (
                    <> @ {formatCurrencyValue(timeCostRate)}</>
                  )}
                  )
                </Typography>
              )}
            </Typography>
          ) : (
            <Typography color={'text.secondary'} variant="body2">
              None
              {props.cost.type === 'TIME' && (
                <Typography
                  color={'text.secondary'}
                  variant="body2"
                  component="span"
                >
                  {' '}
                  ({props.cost.costData.employee.firstName}{' '}
                  {props.cost.costData.employee.lastName},{' '}
                  <MinuteDuration
                    minutes={props.cost.costData.minutes}
                    variant="body2"
                    component="span"
                  />
                  {props.canViewRates && props.cost.costData.rate && (
                    <> @ {formatCurrencyValue(timeCostRate)}</>
                  )}
                  )
                </Typography>
              )}
            </Typography>
          )}
        </TableCell>
        <TableCell align={'right'}>
          {props.cost.type === 'TIME' && !props.canViewRates ? (
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          ) : (
            <Typography
              variant="body2"
              color={
                props.cost.amount === null ? 'text.secondary' : 'text.primary'
              }
            >
              {formatCurrencyValue(props.cost.amount, {
                placeholder: 'Not set',
              })}
            </Typography>
          )}
        </TableCell>
        <TableCell align={'center'}>
          <CostInfoTooltip
            createdBy={props.cost.createdBy}
            createdAt={props.cost.createdAt}
            lastUpdatedBy={props.cost.lastUpdatedBy}
            lastUpdatedAt={props.cost.lastUpdatedAt}
            showAsButton={false}
          />
        </TableCell>
        <TableCell align={'center'}>
          <Stack
            spacing={1}
            direction="row"
            alignItems="center"
            justifyContent="center"
          >
            <IconButton
              size="small"
              onClick={editDialog.open}
              disabled={props.disabled}
              aria-label="Edit"
            >
              <EditRounded fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={deleteDialog.open}
              disabled={props.disabled}
              aria-label="Delete"
            >
              <DeleteRounded fontSize="small" />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>
    </>
  );
}

interface HoursRowProps {
  /**
   * The key of the project to which the cost belongs.
   */
  project: SparseProject;

  /**
   * A project cost to display in the table.
   */
  cost: ProjectCost;

  /**
   * If true, the row is disabled.
   */
  disabled?: boolean;

  /**
   * Whether the user can view time rates
   */
  canViewRates: boolean;
}

export function ProjectCostTableHoursRow(props: HoursRowProps) {
  const editDialog = useDisclosure();
  const deleteDialog = useDisclosure();
  const queryClient = useQueryClient();

  const deleteProjectCostMutation = useMutation({
    mutationFn: () => {
      return deleteProjectCost(props.project.key, props.cost.key);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['project', props.project.key],
      });
      queryClient.invalidateQueries({
        queryKey: ['time-record-aggregate'],
      });
      queryClient.invalidateQueries({
        queryKey: ['time-records'],
      });

      deleteDialog.close();
    },
  });

  const handleClickDelete = () => {
    deleteProjectCostMutation.mutate();
  };
  if (props.cost.type !== 'TIME') {
    return null;
  }
  const timeCostRate = toTimeCostRate(props.cost);
  return (
    <>
      <SupportSnackbar
        isOpen={deleteProjectCostMutation.isError}
        onClose={deleteProjectCostMutation.reset}
        message="An error occurred while deleting the cost."
      />
      <TimeRecordDialog
        initialProject={props.project}
        initialTimeRecord={{
          id: props.cost.id,
          key: props.cost.key,
          category: props.cost.category || '',
          totalAmount: props.cost.amount,
          totalMinutes: props.cost.costData.minutes,
          rate: timeCostRate,
          description: props.cost.description,
          incurredOn: props.cost.costIncurredOn,
          worker: props.cost.costData.employee,
          attachedProject: props.cost.attachedToProject,
        }}
        isOpen={editDialog.isOpen}
        onClose={editDialog.close}
        invalidationKeys={[
          ['project', props.project.key, 'cost'],
          ['time-record-aggregate'],
          ['time-records'],
        ]}
      />
      <Dialog
        fullWidth
        maxWidth="sm"
        open={deleteDialog.isOpen}
        onClose={deleteDialog.close}
      >
        <DialogTitle>Delete cost</DialogTitle>
        <DialogContent dividers>
          <Typography marginBottom={1}>
            Are you sure you want to delete this cost? The cost will be
            permanently removed from the project.
          </Typography>
          <Typography>
            (Note: your audit log will continue to show a record of the deleted
            cost.)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={deleteDialog.close}>
            No, cancel
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            onClick={handleClickDelete}
          >
            Yes, delete
          </Button>
        </DialogActions>
      </Dialog>
      <TableRow>
        <TableCell>
          <Typography variant="body2">
            {props.cost.costData.employee.firstName}{' '}
            {props.cost.costData.employee.lastName}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {props.cost.costIncurredOn.toFormat('MMM d, yyyy')}
          </Typography>
        </TableCell>
        <TableCell>
          {props.cost.description ? (
            <Typography variant="body2">{props.cost.description}</Typography>
          ) : (
            <Typography color={'text.secondary'} variant="body2">
              None
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <MinuteDuration
            minutes={props.cost.costData.minutes}
            variant="body2"
          />
        </TableCell>
        {props.canViewRates && (
          <TableCell align={'right'}>
            <Typography
              variant="body2"
              color={
                props.cost.costData.rate === null
                  ? 'text.secondary'
                  : 'text.primary'
              }
            >
              {formatCurrencyValue(timeCostRate, { placeholder: 'Not set' })}
            </Typography>
          </TableCell>
        )}
        {props.canViewRates && (
          <TableCell align={'right'}>
            <Typography
              variant="body2"
              color={
                props.cost.amount === null ? 'text.secondary' : 'text.primary'
              }
            >
              {formatCurrencyValue(props.cost.amount, {
                placeholder: 'Not set',
              })}
            </Typography>
          </TableCell>
        )}
        <TableCell align={'center'}>
          <CostInfoTooltip
            createdBy={props.cost.createdBy}
            createdAt={props.cost.createdAt}
            lastUpdatedBy={props.cost.lastUpdatedBy}
            lastUpdatedAt={props.cost.lastUpdatedAt}
            showAsButton={false}
          />
        </TableCell>
        <TableCell align={'center'}>
          <Stack
            spacing={1}
            direction="row"
            alignItems="center"
            justifyContent="center"
          >
            <IconButton
              size="small"
              onClick={editDialog.open}
              disabled={props.disabled}
              aria-label="Edit"
            >
              <EditRounded fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={deleteDialog.open}
              disabled={props.disabled}
              aria-label="Delete"
            >
              <DeleteRounded fontSize="small" />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>
    </>
  );
}
